import { collection, updateDoc, deleteDoc, doc, getDocs, getDoc, query, orderBy, runTransaction, where, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import StorageService from "./storage_service";
import { memberToFirestore, memberDataFromFirestore } from "../models/member";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "../services/collections";

/**
 * MemberService
 * Centraliza as operações de banco de dados para o módulo de Clientes (Membros).
 * 
 * Todas as leituras passam por `memberDataFromFirestore` para garantir
 * que os dados retornados estejam no formato flat do formulário React:
 * - address: objeto { street, number, ... } → campos flat (zipCode, address=street, number, ...)  
 * - gender/maritalStatus/status/membershipStatus: { id, label } → string ID
 * - Timestamps → Date / ISO string
 */
const MemberService = {
    /**
     * Busca todos os clientes de um tenant.
     */
    getMembers: async (idTenant) => {
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS),
                orderBy("fullName", "asc")
            );
            const querySnapshot = await getDocs(q);
            const members = [];
            querySnapshot.forEach((docSnap) => {
                members.push(memberDataFromFirestore(docSnap.id, docSnap.data()));
            });
            return members;
        } catch (error) {
            console.error("Erro ao listar membros:", error);
            throw error;
        }
    },

    /**
     * Busca apenas os clientes ativos de um tenant.
     * Útil para o quiosque e listagens rápidas.
     */
    getActiveMembers: async (idTenant) => {
        return MemberService.getMembersByStatus(idTenant, "active");
    },

    /**
     * Busca membros por status diretamente no banco de dados para evitar vazamento de leituras.
     */
    getMembersByStatus: async (idTenant, statusFilter = "all") => {
        try {
            const constraints = [];
            if (statusFilter !== "all") {
                constraints.push(where("status", "in", [
                    statusFilter,
                    { id: statusFilter, label: statusFilter === "active" ? "Ativo" : statusFilter === "inactive" ? "Inativo" : statusFilter }
                ]));
            }

            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS),
                ...constraints
            );

            const querySnapshot = await getDocs(q);
            let members = [];

            querySnapshot.forEach((docSnap) => {
                members.push(memberDataFromFirestore(docSnap.id, docSnap.data()));
            });

            if (statusFilter !== "all") {
                members = members.filter(m => m.status === statusFilter);
            } else {
                // Exclui membros com soft delete das listagens gerais
                members = members.filter(m => m.status !== "deleted");
            }

            return members.sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
        } catch (error) {
            console.error("Erro ao listar membros filtrados:", error);
            throw error;
        }
    },

    getMemberById: async (idTenant, idMember) => {
        try {
            const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
            const memberSnap = await getDoc(memberRef);
            if (!memberSnap.exists()) return null;
            return memberDataFromFirestore(memberSnap.id, memberSnap.data());
        } catch (error) {
            console.error("Erro ao buscar membro por ID:", error);
            throw error;
        }
    },

    /**
     * Busca um cliente pelo seu idMember (ID sequencial).
     */
    getMemberByMemberNumber: async (idTenant, idMember) => {
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS),
                where("memberNumber", "==", idMember)
            );
            const querySnapshot = await getDocs(q);
            if (querySnapshot.empty) return null;

            const docSnap = querySnapshot.docs[0];
            return memberDataFromFirestore(docSnap.id, docSnap.data());
        } catch (error) {
            console.error("Erro ao buscar membro pelo idMember:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza um cliente com ID sequencial (idMember).
     */
    save: async (formData, idTenant, authUser, existingMembers = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            if (formData.id) {
                // UPLOAD DE IMAGEM
                let finalPhotoUrl = formData.photoUrl;
                if (finalPhotoUrl && finalPhotoUrl.startsWith('data:image')) {
                    const path = `tenants/${idTenant}/members/${formData.id}/profile.jpg`;
                    finalPhotoUrl = await StorageService.uploadBase64Image(path, finalPhotoUrl);
                    formData.photoUrl = finalPhotoUrl;
                }

                // UPDATE
                const memberData = memberToFirestore(formData, idTenant, authUser);
                const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, formData.id);
                const oldMember = existingMembers.find(m => m.id === formData.id) || {};
                await updateDoc(memberRef, memberData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldMember, memberData);
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.MEMBERS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: formData.id,
                    entityName: memberData.fullName,
                    description: `Atualizou o cliente "${memberData.fullName}"`,
                    details: changes
                });

                return { id: formData.id, ...memberData };
            } else {
                // CREATE com Transação para ID Sequencial
                const counterRef = doc(db, "tenants", idTenant, COLLECTIONS.COUNTERS, "members");
                const membersCol = collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS);
                const newDocRef = doc(membersCol);

                let nextId = 1;
                let idMemberStr = "0001";

                // UPLOAD DE IMAGEM (FEITO FORA DA TRANSAÇÃO PARA EVITAR RETENTATIVAS DUPLICADAS)
                let finalPhotoUrl = formData.photoUrl;
                if (finalPhotoUrl && finalPhotoUrl.startsWith('data:image')) {
                    const path = `tenants/${idTenant}/members/${newDocRef.id}/profile.jpg`;
                    finalPhotoUrl = await StorageService.uploadBase64Image(path, finalPhotoUrl);
                    formData.photoUrl = finalPhotoUrl;
                }

                const savedMember = await runTransaction(db, async (transaction) => {
                    const counterSnap = await transaction.get(counterRef);

                    if (counterSnap.exists()) {
                        nextId = (counterSnap.data().lastId || 0) + 1;
                    }

                    idMemberStr = String(nextId).padStart(4, "0");

                    const memberDataWithId = {
                        ...memberToFirestore(formData, idTenant, authUser),
                        memberNumber: idMemberStr
                    };

                    transaction.set(newDocRef, memberDataWithId);
                    transaction.set(counterRef, { lastId: nextId }, { merge: true });

                    return { id: newDocRef.id, ...memberDataWithId };
                });

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.MEMBERS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: savedMember.id,
                    entityName: savedMember.fullName,
                    description: `Criou o cliente "${savedMember.fullName}" (ID: ${idMemberStr})`
                });

                return savedMember;
            }
        } catch (error) {
            console.error("Erro no MemberService.save:", error);
            throw error;
        }
    },

    /**
     * Verifica se existem vínculos que impedem a exclusão do membro.
     * Retorna lista de bloqueadores ativos.
     */
    checkDeletionBlockers: async (idTenant, idMember) => {
        const blockers = [];

        const [enrollSnap, membershipSnap, financialSnap] = await Promise.all([
            getDocs(query(
                collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS),
                where("idMember", "==", idMember),
                where("status", "==", "active"),
                where("type", "==", "regular")
            )),
            getDocs(query(
                collection(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS),
                where("idMember", "==", idMember),
                where("status", "in", ["active", "suspended"])
            )),
            getDocs(query(
                collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS),
                where("idMember", "==", idMember),
                where("status", "==", "pending")
            )),
        ]);

        if (!enrollSnap.empty) {
            const names = enrollSnap.docs.map(d => d.data().className || d.data().idClass).filter(Boolean);
            blockers.push({ type: "enrollment", label: "Matrícula ativa", detail: names.join(", ") || `${enrollSnap.size} turma(s)` });
        }
        if (!membershipSnap.empty) {
            const names = membershipSnap.docs.map(d => d.data().membershipName || "Plano").filter(Boolean);
            blockers.push({ type: "membership", label: "Plano ativo", detail: names.join(", ") || `${membershipSnap.size} plano(s)` });
        }
        if (!financialSnap.empty) {
            blockers.push({ type: "financial", label: "Fatura(s) pendente(s)", detail: `${financialSnap.size} fatura(s) em aberto` });
        }

        return blockers;
    },

    /**
     * Soft delete: marca o membro como excluído sem remover o documento.
     * O trigger onMemberUpdated cuida de remover das sessões futuras e desativar matrículas.
     */
    softDelete: async (idTenant, idMember, authUser, memberName) => {
        if (!idTenant || !idMember) throw new Error("ID do Tenant e do membro são obrigatórios.");

        const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
        const now = serverTimestamp();
        const actor = { uid: authUser?.uid || "system", name: authUser?.displayName || authUser?.name || authUser?.email || "Sistema" };

        await updateDoc(memberRef, {
            status: "deleted",
            deletedAt: now,
            deletedBy: actor,
            updatedAt: now,
            updatedBy: actor,
        });

        // Arquivar tasks CRM abertas (o trigger não cobre isso)
        const tasksSnap = await getDocs(query(
            collection(db, "tenants", idTenant, COLLECTIONS.TASKS),
            where("idMember", "==", idMember),
            where("status", "in", ["new", "doing", "waiting", "overdue", "scheduled"])
        ));
        if (!tasksSnap.empty) {
            const { writeBatch } = await import("firebase/firestore");
            const batch = writeBatch(db);
            tasksSnap.docs.forEach(d => batch.update(d.ref, { status: "archived", updatedAt: now, updatedBy: actor }));
            await batch.commit();
        }

        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.MEMBERS,
            action: AUDIT_ACTIONS.DELETE,
            entityId: idMember,
            entityName: memberName,
            description: `Excluiu (soft delete) o cliente "${memberName || idMember}"`,
        });

        return true;
    },

    /**
     * Remove um cliente.
     */
    delete: async (idMember, idTenant, authUser, memberName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.MEMBERS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: idMember,
                entityName: memberName,
                description: `Excluiu o cliente "${memberName || idMember}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no MemberService.delete:", error);
            throw error;
        }
    },

    activateMember: async (idTenant, idMember, authUser, memberName = "") => {
        if (!idTenant || !idMember) throw new Error("ID do Tenant e do membro são obrigatórios.");

        const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);

        try {
            await updateDoc(memberRef, {
                status: "active",
                activatedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                updatedBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.name || authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                }
            });

            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.MEMBERS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: idMember,
                entityName: memberName || idMember,
                description: `Ativou manualmente a conta do cliente "${memberName || idMember}"`,
                details: { status: "active", operacao: "manual" }
            });

            return true;
        } catch (error) {
            console.error("Erro no MemberService.activateMember:", error);
            throw error;
        }
    },

    deactivateMember: async (idTenant, idMember, authUser, memberName = "") => {
        if (!idTenant || !idMember) throw new Error("ID do Tenant e do membro são obrigatórios.");

        const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);

        try {
            await updateDoc(memberRef, {
                status: "inactive",
                deactivatedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                updatedBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.name || authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                }
            });

            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.MEMBERS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: idMember,
                entityName: memberName || idMember,
                description: `Inativou manualmente a conta do cliente "${memberName || idMember}"`,
                details: { status: "inactive", operacao: "manual" }
            });

            return true;
        } catch (error) {
            console.error("Erro no MemberService.deactivateMember:", error);
            throw error;
        }
    }
};

export default MemberService;
